/**
 * Màn "Liên hệ" (hợp đồng A0 liên hệ mục 5): danh sách yêu cầu gửi từ form Liên hệ
 * trên trang giới thiệu. Dữ liệu của nền tảng — chỉ `platform.manage` (BaoVeTuyen ở
 * tuyến /quan-tri/lien-he; khu /quan-tri chặn tài khoản xã).
 *
 * Lọc trạng thái + từ khoá, phân trang PHÍA MÁY CHỦ (mặc định 20 dòng). Nội dung rút
 * gọn trong bảng, bấm "Xem đủ"/"Xử lý" mở hộp xem toàn văn, đổi trạng thái và ghi chú.
 * Nội dung do người ngoài gửi: chỉ hiển thị dạng chữ (React tự escape), không dùng HTML.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import {
  CAC_TRANG_THAI_LIEN_HE,
  lienHeApi,
  type DongLienHe,
  type TrangLienHe,
  type TrangThaiLienHe,
} from '../api/lien-he-api';
import { LoiApi } from '../api/goi-chung';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { baoLienHeDaDoi } from '../trang-thai/lien-he-moi';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag, TruongLoc, type KieuTag } from '../thanh-phan/MauGiaoDien';
import './quan-tri.css';

const t = viQuanTri.lienHe;

/** Lớp API dùng ở màn hình — ghi đè được trong kiểm thử. */
export type ApiLienHe = Pick<typeof lienHeApi, 'danhSach' | 'capNhat'>;

/** Số ký tự nội dung hiện trong bảng trước khi rút gọn. */
export const DO_DAI_RUT_GON = 80;
const GHI_CHU_TOI_DA = 1000;

const KIEU_TAG: Record<TrangThaiLienHe, KieuTag> = { moi: 'xanh', dang_xu_ly: 'vang', da_xu_ly: 'ok' };

const dinhDangThoiGian = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function thoiGianLienHe(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dinhDangThoiGian.format(d);
}

/** Rút gọn theo ký tự (không cắt giữa cặp thay thế UTF-16). */
export function rutGon(s: string, toiDa = DO_DAI_RUT_GON): { chu: string; daCat: boolean } {
  const kyTu = Array.from(s);
  if (kyTu.length <= toiDa) return { chu: s, daCat: false };
  return { chu: `${kyTu.slice(0, toiDa).join('').trimEnd()}…`, daCat: true };
}

function moTaLoi(err: unknown): string {
  if (!(err instanceof LoiApi)) return t.loi.macDinh;
  const goc = (t.loi as Record<string, string>)[err.ma] ?? t.loi.macDinh;
  return err.message && err.message !== goc ? `${goc} ${err.message}` : goc;
}

/* ------------------------------ Hộp xử lý ------------------------------ */

interface HopXuLyProps {
  dong: DongLienHe;
  api: Pick<ApiLienHe, 'capNhat'>;
  onHuy: () => void;
  onXong: (d: DongLienHe) => void;
}

function HopXuLy({ dong, api, onHuy, onXong }: HopXuLyProps): ReactNode {
  const [trangThai, datTrangThai] = useState<TrangThaiLienHe>(dong.trangThai);
  const [ghiChu, datGhiChu] = useState(dong.ghiChuXuLy ?? '');
  const [loi, datLoi] = useState<string | null>(null);
  const [dangLuu, datDangLuu] = useState(false);
  const idTieuDe = useId();
  const oDau = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    oDau.current?.focus();
  }, []);

  const luu = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const ghiChuGui = ghiChu.trim();
    if (Array.from(ghiChuGui).length > GHI_CHU_TOI_DA) {
      datLoi(t.hop.loiGhiChuDai);
      return;
    }
    datLoi(null);
    datDangLuu(true);
    try {
      // Luôn gửi ghi chú: '' nghĩa là xoá ghi chú cũ (hợp đồng mục 2).
      const kq = await api.capNhat(dong.id, { trangThai, ghiChuXuLy: ghiChuGui });
      onXong(kq ?? { ...dong, trangThai, ghiChuXuLy: ghiChuGui || null });
    } catch (err) {
      datLoi(moTaLoi(err));
      datDangLuu(false);
    }
  };

  const nguoiXuLy = dong.nguoiXuLy
    ? dienVao(t.hop.nguoiXuLy, {
        ten: dong.nguoiXuLy.ten ?? t.hop.khongRoTen,
        luc: thoiGianLienHe(dong.xuLyLuc),
      })
    : null;

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai hop-lien-he-cms"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="hop-xu-ly-lien-he"
        onSubmit={(e) => void luu(e)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !dangLuu) onHuy();
        }}
        noValidate
      >
        <h2 id={idTieuDe}>{t.hop.tieuDe}</h2>
        <dl className="lien-he-cms__thong-tin">
          <dt>{t.hop.nguoiGui}</dt>
          <dd>
            <strong>{dong.hoTen}</strong>
            {dong.coQuan ? ` — ${dong.coQuan}` : null}
            {dong.diaPhuong ? ` (${dong.diaPhuong})` : null}
            <br />
            <a href={`tel:${dong.dienThoai.replace(/[^\d+]/g, '')}`}>{dong.dienThoai}</a>
            {dong.email ? (
              <>
                {' · '}
                <a href={`mailto:${dong.email}`}>{dong.email}</a>
              </>
            ) : null}
          </dd>
          <dt>{t.hop.guiLuc}</dt>
          <dd>{thoiGianLienHe(dong.createdAt)}</dd>
          <dt>{t.hop.noiDung}</dt>
          <dd className="lien-he-cms__noi-dung" data-vung="noi-dung-day-du">
            {dong.noiDung}
          </dd>
        </dl>
        {nguoiXuLy ? <p className="truong__mo-ta">{nguoiXuLy}</p> : null}

        <div className="truong">
          <label htmlFor="lh-trang-thai">{t.hop.trangThai}</label>
          <select
            id="lh-trang-thai"
            ref={oDau}
            value={trangThai}
            onChange={(e) => datTrangThai(e.target.value as TrangThaiLienHe)}
          >
            {CAC_TRANG_THAI_LIEN_HE.map((tt) => (
              <option key={tt} value={tt}>
                {t.trangThai[tt]}
              </option>
            ))}
          </select>
        </div>
        <div className="truong">
          <label htmlFor="lh-ghi-chu">{t.hop.ghiChu}</label>
          <textarea
            id="lh-ghi-chu"
            rows={3}
            maxLength={GHI_CHU_TOI_DA}
            placeholder={t.hop.goiYGhiChu}
            value={ghiChu}
            onChange={(e) => datGhiChu(e.target.value)}
          />
        </div>
        {loi ? (
          <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="hop-thoai__nut">
          <button type="button" className="nut" onClick={onHuy} disabled={dangLuu}>
            {t.hop.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangLuu}>
            {dangLuu ? t.hop.dangLuu : t.hop.luu}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------- Màn hình -------------------------------- */

export interface QuanTriLienHeProps {
  api?: ApiLienHe;
}

export function QuanTriLienHe({ api = lienHeApi }: QuanTriLienHeProps = {}): ReactNode {
  const [moc, datMoc] = useState(0);
  const [locTrangThai, datLocTrangThai] = useState<'' | TrangThaiLienHe>('');
  const [oTuKhoa, datOTuKhoa] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [dangXuLy, datDangXuLy] = useState<DongLienHe | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);

  const dangLoc = locTrangThai !== '' || tuKhoa !== '';

  const kq = dungTaiDuLieu<TrangLienHe>({
    tai: (signal) =>
      api.danhSach(
        { page: trang, pageSize: soDong, trangThai: locTrangThai || undefined, tuKhoa: tuKhoa || undefined },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [moc, trang, soDong, locTrangThai, tuKhoa],
  });
  const taiLai = (): void => datMoc((x) => x + 1);
  const soMoi = kq.duLieu?.soMoi;

  const xoaLoc = (): void => {
    datLocTrangThai('');
    datOTuKhoa('');
    datTuKhoa('');
    datTrang(1);
  };

  const mo = (d: DongLienHe): void => {
    datThongBao(null);
    datDangXuLy(d);
  };

  const cot: CotBang<DongLienHe>[] = [
    { khoa: 'thoiGian', nhan: t.cot.thoiGian, ve: (d) => thoiGianLienHe(d.createdAt) },
    { khoa: 'hoTen', nhan: t.cot.hoTen, ve: (d) => d.hoTen },
    { khoa: 'coQuan', nhan: t.cot.coQuan, ve: (d) => d.coQuan || '—' },
    {
      khoa: 'dienThoai',
      nhan: t.cot.dienThoai,
      ve: (d) => <a href={`tel:${d.dienThoai.replace(/[^\d+]/g, '')}`}>{d.dienThoai}</a>,
    },
    {
      khoa: 'email',
      nhan: t.cot.email,
      ve: (d) => (d.email ? <a href={`mailto:${d.email}`}>{d.email}</a> : '—'),
    },
    { khoa: 'diaPhuong', nhan: t.cot.diaPhuong, ve: (d) => d.diaPhuong || '—' },
    {
      khoa: 'noiDung',
      nhan: t.cot.noiDung,
      ve: (d) => {
        const rg = rutGon(d.noiDung);
        return (
          <span className="lien-he-cms__rut-gon">
            <span title={rg.daCat ? d.noiDung : undefined}>{rg.chu}</span>
            {rg.daCat ? (
              <button type="button" className="nut-lien-ket" onClick={() => mo(d)}>
                {t.xemDu}
              </button>
            ) : null}
          </span>
        );
      },
    },
    {
      khoa: 'trangThai',
      nhan: t.cot.trangThai,
      ve: (d) => (
        <span data-trang-thai={d.trangThai}>
          <TheTag kieu={KIEU_TAG[d.trangThai] ?? 'trung-tinh'}>
            {t.trangThai[d.trangThai] ?? d.trangThai}
          </TheTag>
        </span>
      ),
    },
    {
      khoa: 'hanhDong',
      nhan: t.cot.hanhDong,
      ve: (d) => (
        <button type="button" className="nut nut--nho" onClick={() => mo(d)}>
          {t.xuLy}
        </button>
      ),
    },
  ];

  return (
    <div className="man-hinh man-hinh--lien-he">
      <header className="man-hinh__dau">
        <div>
          <h1>{t.tieuDe}</h1>
          <p className="chu-thich">{t.moTa}</p>
        </div>
        {soMoi ? (
          <div className="man-hinh__hanh-dong" data-vung="so-lien-he-moi">
            <TheTag kieu="xanh">{dienVao(t.soMoi, { so: soMoi })}</TheTag>
          </div>
        ) : null}
      </header>

      <div className="thanh-loc-mau" role="search" data-vung="thanh-loc">
        <TruongLoc nhan={t.loc.trangThai} htmlFor="lh-loc-trang-thai">
          <select
            id="lh-loc-trang-thai"
            value={locTrangThai}
            onChange={(e) => {
              datLocTrangThai(e.target.value as '' | TrangThaiLienHe);
              datTrang(1);
            }}
          >
            <option value="">{t.loc.tatCa}</option>
            {CAC_TRANG_THAI_LIEN_HE.map((tt) => (
              <option key={tt} value={tt}>
                {t.trangThai[tt]}
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.loc.tuKhoa} htmlFor="lh-loc-tu-khoa">
          <form
            className="man-hinh__hanh-dong"
            onSubmit={(e) => {
              e.preventDefault();
              datTuKhoa(oTuKhoa.trim());
              datTrang(1);
            }}
          >
            <input
              id="lh-loc-tu-khoa"
              type="search"
              maxLength={100}
              placeholder={t.loc.goiYTuKhoa}
              value={oTuKhoa}
              onChange={(e) => datOTuKhoa(e.target.value)}
            />
            <button type="submit" className="nut">
              {t.loc.tim}
            </button>
          </form>
        </TruongLoc>
      </div>

      {thongBao ? <p role="status">{thongBao}</p> : null}

      <BangDuLieu<DongLienHe>
        nhan={t.tieuDe}
        cot={cot}
        dong={kq.duLieu?.items ?? []}
        khoaDong={(d) => d.id}
        lopDong={(d) => (d.trangThai === 'moi' ? 'lien-he-cms__dong--moi' : undefined)}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />

      {dangXuLy ? (
        <HopXuLy
          key={dangXuLy.id}
          dong={dangXuLy}
          api={api}
          onHuy={() => datDangXuLy(null)}
          onXong={(d) => {
            datDangXuLy(null);
            datThongBao(dienVao(t.daCapNhat, { ten: d.hoTen || dangXuLy.hoTen }));
            taiLai();
            baoLienHeDaDoi();
          }}
        />
      ) : null}
    </div>
  );
}
